import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Modal from '../../Shared/Modal';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';
import styles from './ContractBattles.module.css';

const IncomingChallenges = ({ displayToast }: { displayToast: (message: string, type: 'success' | 'error') => void }) => {
  const { mercenaryId, mercenary } = useMercenary();
  const navigate = useNavigate();
  const [incomingChallenges, setIncomingChallenges] = useState<any[]>([]);
  const [showReady, setShowReady] = useState<boolean>(false);
  const [minLevelIncoming, setMinLevelIncoming] = useState<number>(1);
  const [maxLevelIncoming, setMaxLevelIncoming] = useState<number>(50);
  const [showModal, setShowModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<number | null>(null);
  const [strategy, setStrategy] = useState<string>('Normal');
  const [surrenderHp, setSurrenderHp] = useState<number>(0.5);

  // Load per-mercenary prefs when active mercenary changes
  useEffect(() => {
    if (!mercenaryId || !mercenary) return;
    const defaultMin = Math.max(1, mercenary.level - 5);
    const defaultMax = mercenary.level + 5;
    const prefs = loadBattlePrefs(mercenaryId, 'contract-incoming', {
      showReady: false,
      minLevel: defaultMin,
      maxLevel: defaultMax,
      strategy: 'Normal',
      surrenderHp: 0.5,
    });
    setShowReady((prefs.showReady as boolean) ?? false);
    setMinLevelIncoming((prefs.minLevel as number) ?? defaultMin);
    setMaxLevelIncoming((prefs.maxLevel as number) ?? defaultMax);
    setStrategy((prefs.strategy as string) ?? 'Normal');
    setSurrenderHp((prefs.surrenderHp as number) ?? 0.5);
  }, [mercenaryId]);

  // Persist prefs whenever any filter value changes
  useEffect(() => {
    if (!mercenaryId || !mercenary) return;
    saveBattlePrefs(mercenaryId, 'contract-incoming', {
      showReady,
      minLevel: minLevelIncoming,
      maxLevel: maxLevelIncoming,
      strategy,
      surrenderHp,
    });
  }, [mercenaryId, showReady, minLevelIncoming, maxLevelIncoming, strategy, surrenderHp]);

  useEffect(() => {
    const fetchIncomingChallenges = async () => {
      try {
        const response = await axios.get(`/api/contract/incoming/${mercenaryId}`, {
          params: { levelMin: minLevelIncoming, levelMax: maxLevelIncoming },
        });
        setIncomingChallenges(response.data);
      } catch (error) {
        displayToast('Error fetching incoming challenges.', 'error');
      }
    };
    fetchIncomingChallenges();
  }, [mercenaryId, displayToast, minLevelIncoming, maxLevelIncoming]);

  const filteredChallenges = incomingChallenges
    .filter((challenge: any) => {
      const level = challenge.Sender?.level ?? 0;
      const isReady = challenge.senderMercenary?.ready || false;
      return (
        level >= minLevelIncoming &&
        level <= maxLevelIncoming &&
        (!showReady || isReady)
      );
    })
    .sort((a, b) => (b.Sender?.level || 0) - (a.Sender?.level || 0));

    const acceptChallenge = async () => {
        if (selectedChallenge) {
          try {
            const response = await axios.post('/api/battle/contract/accept', {
              challengeId: selectedChallenge,
              mercenaryId,
              strategy,
              surrenderHp,
            });
            const message = response.data?.message || 'Challenge accepted!';
            displayToast(message, 'success');
            setShowModal(false);
            setIncomingChallenges((prev) =>
              prev.filter((challenge) => challenge.id !== selectedChallenge)
            );
            
            if (response.data?.battleId) {
              navigate(`/battle-result/${response.data.battleId}`);
            }
          } catch (error) {
            const errorMessage =
              (axios.isAxiosError(error) && error.response?.data?.message) || 'Failed to accept the challenge.';
            displayToast(errorMessage, 'error');
          }
        }
      };
      
      const rejectChallenge = async (challengeId: number) => {
        try {
          const response = await axios.post('/api/contract/reject', { challengeId, mercenaryId });
          const message = response.data?.message || 'Challenge rejected.';
          displayToast(message, 'success');
          setIncomingChallenges((prev) =>
            prev.filter((challenge) => challenge.id !== challengeId)
          );
        } catch (error) {
          const errorMessage =
            (axios.isAxiosError(error) && error.response?.data?.message) || 'Failed to reject the challenge.';
          displayToast(errorMessage, 'error');
        }
      };

  return (
    <div className={styles.incomingChallenges}>
      {showModal && (
        <Modal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={acceptChallenge}
          title="Accept Challenge"
          message={
            <div className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Combat Strategy</label>
                <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className={styles.formControl}>
                  <optgroup label="Balanced">
                    <option value="Normal">Normal</option>
                    <option value="Normal: Light Attacks">Normal: Light Attacks</option>
                    <option value="Normal: Heavy Attacks">Normal: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Aggressive">
                    <option value="Offensive">Offensive</option>
                    <option value="Offensive: Light Attacks">Offensive: Light Attacks</option>
                    <option value="Offensive: Heavy Attacks">Offensive: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Cautious">
                    <option value="Defensive">Defensive</option>
                    <option value="Defensive: Light Attacks">Defensive: Light Attacks</option>
                    <option value="Defensive: Heavy Attacks">Defensive: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Reckless">
                    <option value="Berserk">Berserk</option>
                    <option value="Berserk: Light Attacks">Berserk: Light Attacks</option>
                    <option value="Berserk: Heavy Attacks">Berserk: Heavy Attacks</option>
                  </optgroup>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Surrender Threshold: {surrenderHp * 100}% HP</label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.9" 
                  step="0.1" 
                  value={surrenderHp} 
                  onChange={(e) => setSurrenderHp(parseFloat(e.target.value))} 
                  className={styles.rangeControl} 
                />
                <div className={styles.rangeLabels}>
                  <span>Death (0%)</span>
                  <span>Cautious (90%)</span>
                </div>
              </div>
            </div>
          }
        />
      )}

      <h3 className={styles.sectionTitle}>Incoming Challenges</h3>
      <div className={styles.filterSection}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={showReady}
            onChange={() => setShowReady(!showReady)}
            className={styles.checkboxInput}
          />
          Show Full Health Only
        </label>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Min Level:</label>
          <input
            type="number"
            value={minLevelIncoming}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setMinLevelIncoming(v);
            }}
            className={styles.filterInput}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Max Level:</label>
          <input
            type="number"
            value={maxLevelIncoming}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setMaxLevelIncoming(v);
            }}
            className={styles.filterInput}
          />
        </div>
      </div>
      
      {filteredChallenges.length === 0 ? (
        <div className={styles.emptyState}>No incoming challenges match your filters.</div>
      ) : (
        <ul className={styles.listContainer}>
          {filteredChallenges.map((challenge) => (
            <li key={challenge.id} className={styles.listItem}>
              <div className={styles.mercInfo}>
                <span className={styles.mercName}>{challenge.senderMercenary?.name}</span>
                <span className={styles.mercLevel}>Lvl {challenge.senderMercenary?.level}</span>
              </div>
              <div className={styles.buttonGroup}>
                <button
                  className={`${styles.actionButton} ${styles.acceptButton}`}
                  onClick={() => {
                    setSelectedChallenge(challenge.id);
                    setShowModal(true);
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={() => rejectChallenge(challenge.id)}
                  className={`${styles.actionButton} ${styles.rejectButton}`}
                >
                  Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default IncomingChallenges;
